<template>
  <div>
    <div class="d-flex align-items-center mb-4">
      <router-link :to="customerPage" class="mr-2">
        <a class="back-to">
          <mdicon name="ChevronLeft" />
        </a>
      </router-link>
      <h3 class="m-0">
        Prices for customer {{ customer?.name || `${customer?.contactName} ${customer?.contactLastName} ` }}
      </h3>
    </div>
    <div class="d-flex justify-content-between mb-4">
      <div class="d-flex gap-2">
        <StripeBtn @click="openModal(carrierPriorityModal)"> Available carriers </StripeBtn>
        <StripeBtn v-if="can('manage', 'prices')" @click="openModal(distributionChannelsModal)">
          Distribution channels
        </StripeBtn>
      </div>
      <div class="d-flex align-items-center justify-content-end">
        <span class="d-inline-block mr-2">Reset everything to default</span>
        <StripeBtn @click="resetEverything"> Reset </StripeBtn>
      </div>
    </div>
    <BasePriceTable
      :prices="prices"
      :default-prices="defaultPrices"
      @update-price="updatePrice"
      @remove-price="removePrice"
    />
    <PageFooter @save-prices="savePrices" @add-price="addPrice" />
    <CarrierPriorityModal :id="carrierPriorityModal" :entity-id="customerId" type="customer" />
    <DistributionChannelsModal :id="distributionChannelsModal" :customer-id="customerId" />
  </div>
</template>

<script lang="ts">
import type { RouterLink } from '@/types/Components/Router'
import type { BasePrice } from '@/types/Models/BasePrice'
import type { Company } from '@/types/Models/Company'
import {
  defineComponent,
  computed,
  ref,
  onBeforeMount,
  onMounted,
  defineAsyncComponent,
  type Ref,
  type ComputedRef,
} from 'vue'
import { useRoute, onBeforeRouteLeave } from 'vue-router'
import { CARRIER_PRIORITY_MODAL, DISTRIBUTION_CHANNELS_MODAL } from '@/const/ModalTypes'
import { CUSTOMER } from '@/const/PriceTypes'
import { none } from '@/services/Helpers'
import { registerModals, openModal } from '@/services/Modal'
import { addPrice, removePrice, resetEverything, savePrices, updatePrice } from '@/services/Prices/PriceActions'
import { useAccountStore } from '@/store/account'
import { useCustomersStore } from '@/store/customers'
import { usePricesStore } from '@/store/prices'
import { STAFF_CUSTOMERS_LIST } from '@/types/router'
import StripeBtn from '@/views/Components/Elements/Buttons/StripeBtn.vue'
import CarrierPriorityModal from '@/views/Modals/Assignment/CarrierPriorityModal.vue'
import DistributionChannelsModal from '@/views/Modals/Staff/DistributionChannelsModal.vue'

export default defineComponent({
  name: 'CustomerPrices',
  components: {
    StripeBtn,
    CarrierPriorityModal,
    DistributionChannelsModal,
    PageFooter: defineAsyncComponent(() => import('@/views/Components/PageParts/PageFooter.vue')),
    BasePriceTable: defineAsyncComponent(() => import('@/views/Staff/BasePrice/BasePriceTable.vue')),
  },
  setup() {
    const pricesStore = usePricesStore()
    const customersStore = useCustomersStore()
    const accountStore = useAccountStore()
    const route = useRoute()

    const ability: ComputedRef = computed(() => accountStore.ability)
    const can = (rule: string, section: string) => ability.value.can(rule, section)

    const defaultPrices: Ref<BasePrice[]> = ref([])

    const prices: ComputedRef<BasePrice[]> = computed(() => pricesStore.prices)
    const changedPrices: ComputedRef<BasePrice[]> = computed(() => pricesStore.changedPrices)
    const customer: ComputedRef<Company | null> = computed(() => customersStore.customer)
    const customerId: ComputedRef<string> = computed(() => String(route.params.id))
    const carrierPriorityModal: string = CARRIER_PRIORITY_MODAL + 'customer'
    const distributionChannelsModal: string = DISTRIBUTION_CHANNELS_MODAL + 'customer'
    const customerPage: RouterLink = { name: STAFF_CUSTOMERS_LIST }

    onBeforeMount(() => {
      customersStore.fetch(customerId.value)
      pricesStore.setType(CUSTOMER)
      pricesStore.setEntityId(customerId.value)
      pricesStore
        .fetchDefault(customerId.value)
        .then((res) => {
          defaultPrices.value = res
          pricesStore
            .fetch(customerId.value)
            .then((result) => {
              if (result.length === 0) {
                pricesStore.setPrices(defaultPrices.value)
              }
            })
            .catch(none)
        })
        .catch(none)
    })

    onMounted(() => {
      registerModals(carrierPriorityModal)
      registerModals(distributionChannelsModal)
    })

    onBeforeRouteLeave((_to, _from) => {
      if (changedPrices.value.length > 0) {
        return new Promise((resolve) => {
          openModal('confirmWrap', {
            title: 'There are unsaved changes',
            message: 'Are you sure you want to cancel?',
            onConfirm: () => {
              pricesStore.reset().catch(none)
              resolve(true)
            },
            onCancel: () => {
              resolve(false)
            },
          })
        })
      }
      return true
    })

    return {
      prices,
      customer,
      customerId,
      customerPage,
      defaultPrices,
      carrierPriorityModal,
      distributionChannelsModal,
      can,
      addPrice,
      openModal,
      savePrices,
      updatePrice,
      removePrice,
      resetEverything,
    }
  },
})
</script>
